import {
  DashboardState,
  EButtonLocation,
  EWFStepType,
  ICRUDFormDiMetadata,
  IDisplayItem,
  IDisplayItemProperty,
  IFormMetadataByStep,
  IFormMetadataByStepResponse,
  IProcessStepScreensInfo,
  IProject,
  IScreenDesign,
  IScreenDesignInfo,
  IStartProcessTrigger,
  IWFStep,
  IWorkFlow,
  IWorkflowScreenSettings,
  RightPanelType
} from "@kuika/kuika-cl-model";
import classNames from "classnames";
import _ from "lodash";
import React from "react";
import { FormService } from "../../../services/form-service";
import { WorkflowScreenService } from "../../../services/workflow-screen-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { getSdInstance } from "../../../shared/utility/designer-context";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import { IPendingUnmanagedAssignment } from "./step-screen-form-wizard-assign-screen-dropdown";
import { StepScreenFormWizardFormsTab } from "./step-screen-form-wizard-forms-tab";
import { StepScreenFormWizardSettingsTab } from "./step-screen-form-wizard-settings-tab";
import "./step-screen-form-wizard.scss";

/** The tabs in the strip's order, so the incoming pane slides in from the side the tab moved to. */
const WIZARD_TAB_ORDER: string[] = ["workflow-screens", "settings"];

export interface IStepScreenFormProps {}

/**
 * How a Save ended, for its button: `saved` rolls to the check (and closes the wizard after it when
 * `closeAfter`), `failed` — an exception, or steps the update-status dialog lists as failed — says
 * Try again, and `nothing` (there was nothing to write) goes back to idle.
 */
interface IWizardSaveResult {
  outcome: "saved" | "failed" | "nothing";
  closeAfter: boolean;
}

const NOTHING_SAVED: IWizardSaveResult = { outcome: "nothing", closeAfter: false };
const SAVE_FAILED: IWizardSaveResult = { outcome: "failed", closeAfter: false };

export interface IStepScreenFormState {
  isContentLoading: boolean;
  isStepSelectionLoading: boolean;
  isUpdatingScreens: boolean;
  /** Where Save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  showUpdateStatusModal: boolean;
  updateStatusResponse: IFormMetadataByStepResponse[];
  progressInfo: {
    currentStep: number;
    totalSteps: number;
    currentProcessId?: string;
    currentProcessName?: string;
    currentStepId?: string;
    currentStepName?: string;
    completedSteps: IFormMetadataByStepResponse[];
    errors: { processId: string; stepId: string; error: string }[];
    isInitializing?: boolean;
    statusMessage?: string;
  } | null;

  selectedProcessId: string | null;
  selectedStepId: string | null;
  formMetadataByStep: IFormMetadataByStep[];
  draftSteps: { processId: string; stepId: string }[];
  isScreenUnmanaged: boolean;
  isScreenDoesNotExist: boolean;
  activeTab: "workflow-screens" | "settings";
  workflowScreensSettings: IWorkflowScreenSettings;
  pendingUnmanagedAssignments: IPendingUnmanagedAssignment[];
  /**
   * Workflow ids whose process takes input parameters AND has no StartProcess
   * trigger anywhere in the project. Populated after the wizard's Save finishes.
   * Sidebar renders a warning icon next to each entry; clicking offers to
   * auto-generate a start screen.
   */
  untriggeredProcessIds: Record<string, true>;
}

class StepScreenFormWizard extends React.PureComponent<IStepScreenFormProps & IWithSDProps, IStepScreenFormState> {
  private static defaultWorkflowScreensSettings: IWorkflowScreenSettings = {
    buttonLocation: EButtonLocation.UnderForm
  };

  private static initialState: IStepScreenFormState = {
    isContentLoading: false,
    isStepSelectionLoading: false,
    isUpdatingScreens: false,
    saveStatus: "idle",
    showUpdateStatusModal: false,
    updateStatusResponse: [],
    progressInfo: null,
    selectedProcessId: null,
    selectedStepId: null,
    formMetadataByStep: [],
    draftSteps: [],
    isScreenDoesNotExist: false,
    isScreenUnmanaged: false,
    activeTab: "workflow-screens",
    workflowScreensSettings: this.defaultWorkflowScreensSettings,
    pendingUnmanagedAssignments: [],
    untriggeredProcessIds: {}
  };

  /**
   * Save's order: the ✓ goes up and is held, and only then may the wizard close — the saved
   * assignments and screens are on the wizard itself while it stays open.
   */
  private readonly saveMachine: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props) {
    super(props);
    this.state = { ...StepScreenFormWizard.initialState };
  }

  componentWillUnmount(): void {
    this.saveMachine.dispose();
  }

  componentDidUpdate(prevProps: IStepScreenFormProps & IWithSDProps) {
    if (this.props.sd.isWorkflowScreenStepWizardOpen && !prevProps.sd.isWorkflowScreenStepWizardOpen) {
      const state = { ...StepScreenFormWizard.initialState };

      const project: IProject = this.props.sd.project;

      if (!project) {
        throw new Error("Project not found");
      }

      if (!_.isEmpty(project.workflowScreensSettings)) {
        state.workflowScreensSettings = project.workflowScreensSettings;
      } else {
        state.workflowScreensSettings = { ...StepScreenFormWizard.defaultWorkflowScreensSettings };
      }

      // Auto-select the step if initial step info is provided
      if (
        this.props.sd.workflowScreenStepWizardInitialProcessId &&
        this.props.sd.workflowScreenStepWizardInitialStepId
      ) {
        state.selectedProcessId = this.props.sd.workflowScreenStepWizardInitialProcessId;
        state.selectedStepId = this.props.sd.workflowScreenStepWizardInitialStepId;
      }

      this.setState(state, () => {
        // Auto-load the selected step's form if initial step info is provided
        if (
          this.props.sd.workflowScreenStepWizardInitialProcessId &&
          this.props.sd.workflowScreenStepWizardInitialStepId
        ) {
          this.onChangeSelectedStep(
            this.props.sd.workflowScreenStepWizardInitialProcessId,
            this.props.sd.workflowScreenStepWizardInitialStepId
          );
        }
      });
    }
  }

  removeDraftFormMetadata = (processId: string, stepId: string) => {
    const formMetadataByStep = this.state.formMetadataByStep;
    const index = formMetadataByStep.findIndex((f) => f.processId === processId && f.stepId === stepId);

    if (index !== -1) {
      formMetadataByStep.splice(index, 1);
    }

    // Also remove from draft steps
    const draftSteps = this.state.draftSteps.filter(
      (draft) => !(draft.processId === processId && draft.stepId === stepId)
    );

    this.setState({ formMetadataByStep, draftSteps });
  };

  getStepsWithoutScreens = () => {
    const stepsWithoutScreens: { processId: string; stepId: string; processName: string; stepName: string }[] = [];

    this.props.sd.workFlowList.forEach((workflow: IWorkFlow) => {
      if (!workflow.nodes?.steps) return;

      workflow.nodes.steps.forEach((step: IWFStep) => {
        if (step.stepType !== EWFStepType.ScreenInteraction) return;

        const hasScreen = this.getScreenForStep(workflow.id, step.id);
        const isManaged = this.getIsScreenManaged(workflow.id, step.id);
        const isInDraft = this.state.draftSteps.some(
          (draft) => draft.processId === workflow.id && draft.stepId === step.id
        );

        // Include step if it has no screen or is unmanaged (exclude draft steps)
        if ((!hasScreen || !isManaged) && !isInDraft) {
          stepsWithoutScreens.push({
            processId: workflow.id,
            stepId: step.id,
            processName: workflow.displayName ?? workflow.name,
            stepName: step.name
          });
        }
      });
    });

    return stepsWithoutScreens;
  };

  setScreenUnmanaged = (processId: string, stepId: string) => {
    this.setState({
      isScreenUnmanaged: true,
      isScreenDoesNotExist: false,
      selectedProcessId: processId,
      selectedStepId: stepId
    });
  };

  getProcessName = (processId: string) => {
    const process: IWorkFlow | undefined = this.props.sd.workFlowList.find((wf: IWorkFlow) => wf.id === processId);

    if (!process) {
      return "";
    }

    return process.name;
  };

  getStepName = (processId: string, stepId: string) => {
    const process: IWorkFlow | undefined = this.props.sd.workFlowList.find((wf: IWorkFlow) => wf.id === processId);

    if (!process) {
      return "";
    }

    const step: IWFStep | undefined = this.props.sd.findStepRecursively(process, (s) => s.id === stepId);

    if (!step) {
      return "";
    }

    return step.name;
  };

  getStep = () => {
    if (!this.state.selectedStepId) {
      throw new Error("Step id is not selected");
    }

    const process: IWorkFlow | undefined = this.props.sd.workFlowList.find(
      (wf: IWorkFlow) => wf.id === this.state.selectedProcessId
    );

    if (!process) {
      throw new Error("Process not found");
    }

    const step: IWFStep | undefined = this.props.sd.findStepRecursively(
      process,
      (s) => s.id === this.state.selectedStepId!
    );

    if (!step) {
      throw new Error("Step not found");
    }

    return step;
  };

  getScreenForStep = (processId: string, stepId: string) => {
    if (!stepId) {
      return undefined;
    }

    const { project } = this.props.sd;

    if (!project) {
      return undefined;
    }

    const processStepScreenInfo: IProcessStepScreensInfo | undefined = project.processStepScreenIds
      ?.filter((ps) => ps.processId === processId && ps.stepId === stepId)
      ?.at(-1);

    return this.props.sd.screenList.find((screen) => screen.id === processStepScreenInfo?.screenId);
  };

  getIsFormsMetadataMatchingStep = (formDi: IDisplayItem, processId: string, stepId: string): boolean => {
    if (formDi?.item?.name !== "Form") {
      return false;
    }

    const metadataProp: IDisplayItemProperty | undefined = formDi?.item?.properties?.find(
      (property: IDisplayItemProperty) => property.name === "metadata"
    );

    if (!metadataProp) {
      return false;
    }

    const metadata: ICRUDFormDiMetadata = JSON.parse(metadataProp.symbol.fixedValue) as ICRUDFormDiMetadata;

    return metadata.linkedWorkflowBo?.processId === processId && metadata.linkedWorkflowBo.stepId === stepId;
  };

  getFormDiFromScreen = (displayItems: IDisplayItem[], processId: string, stepId: string): IDisplayItem | undefined => {
    let di: IDisplayItem | undefined;

    displayItems.forEach((diIter: IDisplayItem) => {
      if (di) {
        return;
      }

      if (diIter.item.name === "Form" && this.getIsFormsMetadataMatchingStep(diIter, processId, stepId)) {
        di = diIter;
        return;
      }

      if (diIter.children) {
        di = this.getFormDiFromScreen(diIter.children, processId, stepId);
      }
    });

    return di;
  };

  getIsScreenManaged = (processId: string, stepId: string) => {
    const processStepScreenInfo: IProcessStepScreensInfo | undefined = this.props.sd.project.processStepScreenIds
      ?.filter((ps) => ps.processId === processId && ps.stepId === stepId)
      ?.at(-1);

    return processStepScreenInfo?.isManaged ?? false;
  };

  // Both null is how the rail says "back to the landing page".
  onChangeSelectedStep = async (processId: string | null, stepId: string | null) => {
    try {
      if (!processId || !stepId) {
        this.setState({
          selectedProcessId: null,
          selectedStepId: null,
          isScreenDoesNotExist: false,
          isScreenUnmanaged: false
        });
        return;
      }

      // Show loading immediately when step is clicked
      this.setState({
        isStepSelectionLoading: true,
        selectedProcessId: null,
        selectedStepId: null,
        isScreenUnmanaged: false
      });

      const isLoaded: boolean = !!this.state.formMetadataByStep.find(
        (f) => f.processId === processId && f.stepId === stepId
      );

      if (isLoaded) {
        this.setState({
          isScreenDoesNotExist: false,
          selectedProcessId: processId,
          selectedStepId: stepId,
          isStepSelectionLoading: false
        });
        return;
      }

      const stepScreen: IScreenDesignInfo | undefined = this.getScreenForStep(processId, stepId);

      if (!stepScreen) {
        this.setState({
          isScreenDoesNotExist: !stepScreen,
          selectedProcessId: processId,
          selectedStepId: stepId,
          isStepSelectionLoading: false
        });
        return;
      }
      this.setState({ isScreenDoesNotExist: false });

      const isScreenManaged: boolean = this.getIsScreenManaged(processId, stepId);

      if (!isScreenManaged) {
        this.setScreenUnmanaged(processId, stepId);
        this.setState({ isStepSelectionLoading: false });
        return;
      }

      const isStepScreenCurrentScreen: boolean = this.props.sd.sdId === stepScreen.id;

      let metadata: ICRUDFormDiMetadata;

      if (!isStepScreenCurrentScreen) {
        const response = await WorkflowScreenService.GetFormMetadataOfScreen(stepScreen.id, {
          processId,
          stepId
        });

        if (response.status !== 200 || !response.data) {
          return;
        }

        metadata = response.data;
      } else {
        const screenDesign: IScreenDesign = this.props.sd.getScreenDesign();
        const displayItems: IDisplayItem[] = screenDesign?.displayItems || [];

        const formDi: IDisplayItem | undefined = this.getFormDiFromScreen(displayItems, processId, stepId);

        if (!formDi) {
          return;
        }

        const metadataProp: IDisplayItemProperty | undefined = formDi?.item?.properties?.find(
          (property: IDisplayItemProperty) => property.name === "metadata"
        );

        if (!metadataProp) {
          return;
        }

        metadata = JSON.parse(metadataProp.symbol.fixedValue) as ICRUDFormDiMetadata;
      }

      const syncResponse = await FormService.SyncFormMetadataWithWorkflowBo(metadata);

      if (syncResponse.status !== 200 || !syncResponse.data) {
        return;
      }

      if (syncResponse.data.metadata) {
        metadata = syncResponse.data.metadata;
      }

      this.setFormMetadataByStep(processId, stepId, metadata);
      this.setState({
        selectedProcessId: processId,
        selectedStepId: stepId,
        isStepSelectionLoading: false
      });
    } catch (error) {
      this.setState({ isStepSelectionLoading: false });
    }
  };

  onClose = () => {
    // A close during Save's check ends the check with it; the held close does not run again.
    this.saveMachine.reset();
    this.setState({ ...StepScreenFormWizard.initialState });
    this.props.sd.setIsWorkflowScreenStepWizardOpen(false);
  };

  getProcesses = (): IWorkFlow[] => {
    return this.props.sd.workFlowList;
  };

  setFormMetadataByStep = (processId: string, stepId: string, formMetadata: ICRUDFormDiMetadata) => {
    const formMetadataByStep =
      this.state.formMetadataByStep.length > 0 ? structuredClone(this.state.formMetadataByStep) : [];

    if (!formMetadataByStep.find((f) => f.processId === processId && f.stepId === stepId)) {
      formMetadataByStep.push({
        processId,
        stepId,
        formMetadata
      });
    } else {
      const index = formMetadataByStep.findIndex((f) => f.processId === processId && f.stepId === stepId);
      formMetadataByStep[index] = {
        processId,
        stepId,
        formMetadata
      };
    }

    this.setState({ formMetadataByStep });
  };

  getPendingUnmanagedForStep = (processId: string | null, stepId: string | null): string | undefined => {
    if (!processId || !stepId) return undefined;
    return this.state.pendingUnmanagedAssignments.find((p) => p.processId === processId && p.stepId === stepId)
      ?.screenId;
  };

  onChangePendingUnmanagedAssignment = (processId: string, stepId: string, screenId: string | null) => {
    const next: IPendingUnmanagedAssignment[] = this.state.pendingUnmanagedAssignments.filter(
      // Drop any prior pending for this step, and any prior pending for the same screen on another step
      // (a screen can only be linked to a single step at a time).
      (p) => !(p.processId === processId && p.stepId === stepId) && p.screenId !== screenId
    );

    if (screenId) {
      next.push({ processId, stepId, screenId });
    }

    this.setState({ pendingUnmanagedAssignments: next });
  };

  applyPendingUnmanagedAssignments = async (): Promise<boolean> => {
    const pending = this.state.pendingUnmanagedAssignments;

    if (pending.length === 0) return false;

    const project: IProject = this.props.sd.project;

    if (!project) {
      throw new Error("Project not found");
    }

    const existing: IProcessStepScreensInfo[] = _.cloneDeep(project.processStepScreenIds) ?? [];

    const pendingScreenIds = new Set(pending.map((p) => p.screenId));
    const next: IProcessStepScreensInfo[] = existing
      .filter((p) => p.projectId && p.processId && p.stepId && p.screenId)
      // Each pending screen can only live in one step, so remove any prior bindings for those screens.
      .filter((p) => !pendingScreenIds.has(p.screenId));

    for (const assignment of pending) {
      next.push({
        screenId: assignment.screenId,
        projectId: project.id,
        processId: assignment.processId,
        stepId: assignment.stepId,
        isManaged: false
      });
    }

    project.processStepScreenIds = next;
    await this.props.sd.updateProject(project);
    await this.props.sd.setScreenList();

    this.setState({ pendingUnmanagedAssignments: [] });
    return true;
  };

  onClickCreateWorkflowScreen = async (processId: string, stepId: string) => {
    try {
      const response = await FormService.GetFormMetadataFromWorkflowBo({
        processId,
        stepId
      });

      if (response.status !== 200 || !response.data) {
        return;
      }

      this.setFormMetadataByStep(processId, stepId, response.data);

      // Add to draft steps
      const draftSteps = [...this.state.draftSteps];
      if (!draftSteps.find((draft) => draft.processId === processId && draft.stepId === stepId)) {
        draftSteps.push({ processId, stepId });
      }

      // CREATE and "assign existing" are mutually exclusive — drop any pending existing-screen
      // selection for this step so Save only applies the newly created screen.
      const pendingUnmanagedAssignments = this.state.pendingUnmanagedAssignments.filter(
        (p) => !(p.processId === processId && p.stepId === stepId)
      );

      this.setState({
        isScreenDoesNotExist: false,
        draftSteps,
        pendingUnmanagedAssignments
      });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  onClickCreateAllWorkflowScreens = async () => {
    try {
      if (this.state.isUpdatingScreens) {
        return;
      }

      const stepsWithoutScreens = this.getStepsWithoutScreens();

      if (stepsWithoutScreens.length === 0) {
        KMainFunctions.displayWarningNotification("All steps already have screens.");
        return;
      }

      this.setState({ isUpdatingScreens: true });

      const draftSteps = [...this.state.draftSteps];
      let successCount = 0;
      let failedSteps: string[] = [];
      let firstSuccessfulStep: { processId: string; stepId: string } | null = null;

      // Process each step sequentially to avoid overwhelming the server
      for (const step of stepsWithoutScreens) {
        try {
          const response = await FormService.GetFormMetadataFromWorkflowBo({
            processId: step.processId,
            stepId: step.stepId
          });

          if (response.status === 200 && response.data) {
            this.setFormMetadataByStep(step.processId, step.stepId, response.data);

            // Add to draft steps if not already present
            if (!draftSteps.find((draft) => draft.processId === step.processId && draft.stepId === step.stepId)) {
              draftSteps.push({ processId: step.processId, stepId: step.stepId });
            }

            // Track the first successful creation
            if (successCount === 0) {
              firstSuccessfulStep = { processId: step.processId, stepId: step.stepId };
            }

            successCount++;
          } else {
            failedSteps.push(`${step.processName} - ${step.stepName}`);
          }
        } catch (error) {
          failedSteps.push(`${step.processName} - ${step.stepName}`);
        }
      }

      this.setState({ draftSteps });

      // Show results
      if (successCount > 0) {
        KMainFunctions.displaySuccessNotification(
          `${successCount} screens created successfully${
            failedSteps.length > 0 ? `, ${failedSteps.length} failed` : ""
          }.`
        );

        if (firstSuccessfulStep) {
          this.onChangeSelectedStep(firstSuccessfulStep.processId, firstSuccessfulStep.stepId);
        }
      }

      if (failedSteps.length > 0) {
        KMainFunctions.displayWarningNotification(
          `Failed to create screens for: ${failedSteps.slice(0, 3).join(", ")}${
            failedSteps.length > 3 ? ` and ${failedSteps.length - 3} more` : ""
          }`
        );
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isUpdatingScreens: false });
    }
  };

  reloadScreen = async () => {
    if (this.props.sd.dashboardState === DashboardState.design) {
      await this.props.sd.loadScreen(this.props.sd.sdId, true, true);
    } else if (this.props.sd.dashboardState === DashboardState.workFlow) {
      // VTO: Bu redirection özellikle yapılıyor. loadScreen db'den project state'i update ederken selectedWF boş olması için ekrana dönüş yapılıyor.
      this.props.sd.setDashboardState(DashboardState.design);
      this.props.sd.setActiveRightPanelByClick(RightPanelType.Properties);
      this.props.sd.clearSelectedWf();
      await this.props.sd.loadScreen(this.props.sd.sdId, true, true);
    }
  };

  onClickSaveFormsTab = async (): Promise<IWizardSaveResult> => {
    try {
      // Initialize progress info immediately to show progress screen right away
      const initialProgressInfo = {
        currentStep: 0,
        totalSteps: this.state.formMetadataByStep?.length || 0,
        completedSteps: [],
        errors: [],
        currentProcessName: undefined,
        currentStepName: undefined,
        isInitializing: true
      };

      this.setState({
        isUpdatingScreens: true,
        progressInfo: initialProgressInfo
      });

      const appliedPending = await this.applyPendingUnmanagedAssignments();

      // If the user only picked existing screens (no CREATE drafts), skip the managed-form save pipeline.
      if (!this.state.formMetadataByStep || this.state.formMetadataByStep.length === 0) {
        if (!appliedPending) {
          return NOTHING_SAVED;
        }
        await this.reloadScreen();
        if (this.state.selectedProcessId && this.state.selectedStepId) {
          await this.onChangeSelectedStep(this.state.selectedProcessId, this.state.selectedStepId);
        }
        this.refreshUntriggeredProcessIds();
        // The wizard stays open on the assignments it just saved: Save's check is the confirmation.
        return { outcome: "saved", closeAfter: false };
      }

      await this.props.sd.saveScreen(true);

      const response = await WorkflowScreenService.UpdateWorkflowScreensWithProgress(
        {
          formMetadataBySteps: this.state.formMetadataByStep
        },
        (progress) => {
          this.setState((prevState) => {
            switch (progress.type) {
              case "started":
                // Initial response - connection established, progress will show in forms tab
                return {
                  ...prevState,
                  progressInfo: {
                    currentStep: 0,
                    totalSteps: progress.totalSteps,
                    completedSteps: [],
                    errors: [],
                    isInitializing: true,
                    statusMessage: "Preparing to start..."
                  } as any
                };

              case "initialized":
                // Setup work is complete, about to start processing
                return {
                  ...prevState,
                  progressInfo: {
                    ...prevState.progressInfo,
                    totalSteps: progress.totalSteps,
                    isInitializing: false,
                    statusMessage: "Processing screens..."
                  } as any
                };

              case "progress":
                return {
                  ...prevState,
                  progressInfo: {
                    ...prevState.progressInfo,
                    currentStep: progress.currentStep,
                    currentProcessId: progress.processId,
                    currentProcessName: progress.processName,
                    currentStepId: progress.stepId,
                    currentStepName: progress.stepName
                  } as any
                };

              case "step_completed":
                if (progress.stepResponse) {
                  return {
                    ...prevState,
                    progressInfo: {
                      ...prevState.progressInfo,
                      completedSteps: [...(prevState.progressInfo?.completedSteps || []), progress.stepResponse]
                    } as any
                  };
                }
                return prevState;

              case "step_error":
                return {
                  ...prevState,
                  progressInfo: {
                    ...prevState.progressInfo,
                    errors: [
                      ...(prevState.progressInfo?.errors || []),
                      {
                        processId: progress.processId,
                        stepId: progress.stepId,
                        error: progress.error
                      }
                    ]
                  } as any
                };

              case "completed":
                return {
                  ...prevState,
                  progressInfo: {
                    ...prevState.progressInfo,
                    currentStep: progress.totalSteps,
                    isCompleted: true
                  } as any
                };

              case "error":
                return {
                  ...prevState,
                  progressInfo: {
                    ...prevState.progressInfo,
                    errors: [
                      ...(prevState.progressInfo?.errors || []),
                      {
                        error: progress.error,
                        isFinalError: true
                      }
                    ],
                    isCompleted: true
                  } as any
                };
            }

            return prevState;
          });
        }
      );

      if (response?.formMetadataBySteps && response.formMetadataBySteps.length > 0) {
        if (response.formMetadataBySteps.some((f) => !f?.isSuccess)) {
          this.setState({
            showUpdateStatusModal: true,
            updateStatusResponse: response.formMetadataBySteps
          });
        }

        // Remove successfully saved steps from draft
        const draftSteps = [...this.state.draftSteps];

        for (const formMetadataByStep of response.formMetadataBySteps) {
          if (formMetadataByStep.isSuccess) {
            this.setFormMetadataByStep(
              formMetadataByStep.processId,
              formMetadataByStep.stepId,
              formMetadataByStep.formMetadata
            );

            // Remove from draft steps
            const draftIndex = draftSteps.findIndex(
              (draft) => draft.processId === formMetadataByStep.processId && draft.stepId === formMetadataByStep.stepId
            );
            if (draftIndex !== -1) {
              draftSteps.splice(draftIndex, 1);
            }
          }
        }

        this.setState({ draftSteps });
      }

      await Promise.all([this.props.sd.setScreenList(), this.reloadScreen()]);

      // Clear draft list since screens have been successfully created and reloaded
      this.setState({ draftSteps: [] });

      const untriggered: Record<string, true> = this.refreshUntriggeredProcessIds();

      if (!response.formMetadataBySteps || response.formMetadataBySteps.length === 0) {
        return { outcome: "saved", closeAfter: false };
      }

      // Save's check is the confirmation; a step that failed is listed by the update-status dialog.
      const allSucceeded: boolean = response.formMetadataBySteps.every((f) => f.isSuccess);
      if (!allSucceeded) {
        // The update-status dialog lists which steps failed; their drafts stay for another Save.
        return SAVE_FAILED;
      }
      // Keep the wizard open while there are untriggered-process warnings to act
      // on; only auto-close (after the ✓) once there's nothing left to surface.
      return { outcome: "saved", closeAfter: Object.keys(untriggered).length === 0 };
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      return SAVE_FAILED;
    } finally {
      this.setState({ isUpdatingScreens: false, progressInfo: null });
    }
  };

  /**
   * Recompute which workflows are "untriggered" (take input parameters but no
   * StartProcess action targets them anywhere). Run after a wizard save so the
   * sidebar warning icon reflects the latest project state.
   *
   * Also runs a defensive prune: any trigger entry whose source entity no
   * longer exists is silently dropped (covers screen-delete paths that didn't
   * call the entity-level prune hook).
   */
  private refreshUntriggeredProcessIds = (): Record<string, true> => {
    const workflows = this.props.sd.workFlowList ?? [];
    if (!this.props.sd.project || workflows.length === 0) {
      this.setState({ untriggeredProcessIds: {} });
      return {};
    }

    // Drop orphan entries first (source no longer exists). Sync mutation
    // updates state.project immediately, so the read below sees the clean index.
    getSdInstance().startProcessTriggerSync.pruneOrphans();

    const triggersIndex = this.props.sd.project.startProcessTriggers ?? {};
    const result: Record<string, true> = {};
    for (const wf of workflows) {
      const hasInputParameters = wf.parameters?.some((p) => p.isInput === true);
      if (!hasInputParameters) continue;
      const triggerCount = triggersIndex[wf.id]?.length ?? 0;
      if (triggerCount === 0) {
        result[wf.id] = true;
      }
    }

    this.setState({ untriggeredProcessIds: result });
    return result;
  };

  /**
   * Wizard sidebar's "Yes, generate a start screen" handler. Asks the backend
   * to materialise a CRUDEdit-template screen with a form + Start Process
   * button bound to this workflow's input parameters. Backend also writes the
   * new StartProcess trigger to project.startProcessTriggers — so once the
   * project state is refreshed, the warning icon for this workflow goes away.
   */
  private onConfirmGenerateStartScreen = async (workflowId: string): Promise<void> => {
    this.props.sd.processWithSpinner(async () => {
      try {
        const workflow = (this.props.sd.workFlowList ?? []).find((w) => w.id === workflowId);
        const screenName =
          workflow?.displayName || workflow?.name
            ? `Start ${(workflow.displayName ?? workflow.name).trim()}`
            : undefined;

        const res = await WorkflowScreenService.CreateWorkflowStartScreen({
          processId: workflowId,
          screenName
        });

        if (!res || res.status !== 200 || !res.data?.screenId) {
          return;
        }

        const { screenId, actionId } = res.data;

        // Refresh screen list so the new start screen appears in the project.
        await this.props.sd.setScreenList();

        // Backend has already written the new StartProcess trigger to project.json.
        // Mirror it into local state.project.startProcessTriggers so the wizard's
        // warning state recomputes immediately without a full project reload.
        if (actionId && screenId) {
          const project = this.props.sd.project;
          if (project) {
            const next: Record<string, IStartProcessTrigger[]> = { ...(project.startProcessTriggers ?? {}) };
            const list = next[workflowId] ? [...next[workflowId]] : [];
            if (!list.some((t) => t.sourceId === screenId && t.actionId === actionId)) {
              list.push({ sourceType: "Screen", sourceId: screenId, actionId });
            }
            next[workflowId] = list;
            await this.props.sd.updateProject({ ...project, startProcessTriggers: next });
          }
        }

        this.refreshUntriggeredProcessIds();
        KMainFunctions.displaySuccessNotification(`Start screen created${screenName ? `: ${screenName}` : ""}.`);
      } catch (error) {
        KMainFunctions.exceptionHandler(error);
      }
    });
  };

  onClickSaveSettingsTab = async (): Promise<IWizardSaveResult> => {
    try {
      const project: IProject = this.props.sd.project;

      if (!project) {
        return NOTHING_SAVED;
      }

      project.workflowScreensSettings = { ...this.state.workflowScreensSettings };
      await this.props.sd.updateProject(project);
      const formsResult: IWizardSaveResult = await this.onClickSaveFormsTab();
      // The settings were written even when the forms had nothing to add.
      if (formsResult.outcome === "nothing") {
        return { outcome: "saved", closeAfter: false };
      }
      return formsResult;
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      return SAVE_FAILED;
    }
  };

  onClickSave = async (): Promise<void> => {
    // Not while a Save runs, and not during its check: a second press would write everything again.
    if (!this.saveMachine.canStart) {
      return;
    }

    this.saveMachine.start();
    let result: IWizardSaveResult = NOTHING_SAVED;
    if (this.state.activeTab === "settings") {
      result = await this.onClickSaveSettingsTab();
    } else {
      result = await this.onClickSaveFormsTab();
    }

    if (result.outcome === "saved") {
      this.saveMachine.succeed(result.closeAfter ? this.onClose : undefined);
    } else if (result.outcome === "failed") {
      this.saveMachine.fail();
    } else {
      this.saveMachine.reset();
    }
  };

  /** Save's button: its own status, or busy while CREATE ALL SCREENS holds the same lock. */
  getSaveButtonStatus = (): KuikaButtonStatus => {
    if (this.state.saveStatus === "idle" && this.state.isUpdatingScreens) {
      return "loading";
    }
    return this.state.saveStatus;
  };

  getFormMetadataFormContent = () => {
    if (!this.state.selectedProcessId || !this.state.selectedStepId) {
      return null;
    }

    if (this.state.isScreenDoesNotExist) {
      return null;
    }

    const stepFormMetadata = this.state.formMetadataByStep.find(
      (f) => f.processId === this.state.selectedProcessId && f.stepId === this.state.selectedStepId
    );

    if (!stepFormMetadata) {
      return null;
    }

    return stepFormMetadata.formMetadata;
  };

  onMetadataChange = (metadata: ICRUDFormDiMetadata) => {
    if (!this.state.selectedStepId || !this.state.selectedProcessId) {
      return;
    }

    this.setFormMetadataByStep(this.state.selectedProcessId, this.state.selectedStepId, metadata);
  };

  /** True while a save is walking the steps — the tabs stand down for the progress panel. */
  getIsProgressActive = (): boolean => {
    return !!this.state.progressInfo && this.state.progressInfo.totalSteps > 0;
  };

  onChangeActiveTab = (value: string): void => {
    this.setState({ activeTab: value === "settings" ? "settings" : "workflow-screens" });
  };

  /** What the dialog's spinner says it is waiting on, or nothing for a plain load. */
  getLoadingLabel = (): string | undefined => {
    if (this.getIsProgressActive() && this.state.progressInfo) {
      const processName: string = this.state.progressInfo.currentProcessName || "Unknown";
      const stepName: string = this.state.progressInfo.currentStepName || "Unknown";
      return `Updating screens... ${this.state.progressInfo.currentStep}/${this.state.progressInfo.totalSteps} - ${processName} > ${stepName}`;
    }

    if (this.state.isUpdatingScreens) {
      return "Preparing screens for update...";
    }

    return undefined;
  };

  renderTabs = (): React.ReactNode => {
    // The tabs stand down while a save walks the steps: there is one thing happening.
    if (this.getIsProgressActive()) {
      return null;
    }

    return (
      <div className="kuika-step-screen-wizard__tabs">
        <KuikaTabs
          value={this.state.activeTab}
          onChange={this.onChangeActiveTab}
          dataTestId="workflowScreensWizardTabs"
          options={[
            {
              value: "workflow-screens",
              dataTestId: "workflowScreensFormsTab",
              label: (
                <span className="kuika-flex-center--gap-xs">
                  <KuikaSvg name="layout-rows" width={16} height={16} />
                  Forms
                </span>
              )
            },
            {
              value: "settings",
              dataTestId: "workflowScreensSettingsTab",
              label: (
                <span className="kuika-flex-center--gap-xs">
                  <KuikaSvg name="settings" width={16} height={16} />
                  Settings
                </span>
              )
            }
          ]}
        />
      </div>
    );
  };

  updateWorkflowScreensSettings = (settings: IWorkflowScreenSettings) => {
    this.setState({ workflowScreensSettings: settings });
  };

  onCloseUpdateStatusModal = () => {
    this.setState({ showUpdateStatusModal: false, updateStatusResponse: [] });
  };

  render() {
    if (!this.props.sd.isWorkflowScreenStepWizardOpen) {
      return null;
    }

    return (
      <>
        <KModal
          showModal
          header="Workflow Screens"
          closeModal={this.onClose}
          width={"calc(100vw - (100vw / 5))"}
          className="kuika-step-screen-wizard"
          isLoading={!this.props.sd.isProjectLoaded}
          loadingLabel={this.getLoadingLabel()}
          // Two views share the dialog: the tabs, and the progress a Save walks through while it
          // writes the steps. Switching between them is a step change — the old view fades out
          // and the new one comes in instead of cutting.
          stepKey={this.getIsProgressActive() ? "progress" : "tabs"}
          showFullScreenButton
          footer={
            <KModalFooter
              submitLabel="Save"
              submitTestId="workflowScreensSaveButton"
              onSubmit={this.onClickSave}
              status={this.getSaveButtonStatus()}
              onCancel={this.onClose}
            />
          }
        >
          {this.renderTabs()}
          {/* Both tabs stay mounted (`keepMounted`): the one that is not open keeps its own
              state, such as which of the rail's processes are expanded, for when it is opened
              again. */}
          <KuikaTabContent
            className="kuika-step-screen-wizard__panes"
            activeKey={this.state.activeTab}
            order={WIZARD_TAB_ORDER}
            keepMounted
          >
            <StepScreenFormWizardFormsTab
              key="workflow-screens"
              processes={this.getProcesses()}
              formMetadata={this.getFormMetadataFormContent()}
              onChangeSelectedStep={this.onChangeSelectedStep}
              onClickCreateWorkflowScreen={this.onClickCreateWorkflowScreen}
              onClickCreateAllWorkflowScreens={this.onClickCreateAllWorkflowScreens}
              selectedProcessId={this.state.selectedProcessId}
              selectedStepId={this.state.selectedStepId}
              getScreenForStep={this.getScreenForStep}
              getIsScreenManaged={this.getIsScreenManaged}
              getStepsWithoutScreens={this.getStepsWithoutScreens}
              draftSteps={this.state.draftSteps}
              isScreenDoesNotExist={this.state.isScreenDoesNotExist}
              isScreenUnmanaged={this.state.isScreenUnmanaged}
              onMetadataChange={this.onMetadataChange}
              reloadScreen={this.reloadScreen}
              removeDraftFormMetadata={this.removeDraftFormMetadata}
              setScreenUnmanaged={this.setScreenUnmanaged}
              progressInfo={this.state.progressInfo}
              isStepSelectionLoading={this.state.isStepSelectionLoading}
              pendingUnmanagedAssignments={this.state.pendingUnmanagedAssignments}
              pendingUnmanagedScreenId={this.getPendingUnmanagedForStep(
                this.state.selectedProcessId,
                this.state.selectedStepId
              )}
              onChangePendingUnmanagedAssignment={this.onChangePendingUnmanagedAssignment}
              untriggeredProcessIds={this.state.untriggeredProcessIds}
              onConfirmGenerateStartScreen={this.onConfirmGenerateStartScreen}
            />
            <StepScreenFormWizardSettingsTab
              key="settings"
              workflowScreensSettings={this.state.workflowScreensSettings}
              updateWorkflowScreensSettings={this.updateWorkflowScreensSettings}
            />
          </KuikaTabContent>
        </KModal>
        {/* A sibling, not a child: it reports on the save the dialog behind it just ran. */}
        <KModal
          showModal={this.state.showUpdateStatusModal}
          header="Update Status"
          closeModal={this.onCloseUpdateStatusModal}
          width={560}
          className="kuika-step-screen-status"
          footer={
            <KModalFooter
              submitLabel="Ok"
              submitTestId="workflowScreensStatusOk"
              onSubmit={this.onCloseUpdateStatusModal}
            />
          }
        >
          <ul className="kuika-step-screen-status__list">
            {this.state.updateStatusResponse.map((response: IFormMetadataByStepResponse) => {
              const isSuccess: boolean = !!response?.isSuccess;

              return (
                <li
                  className="kuika-step-screen-status__row"
                  key={`status-${response?.processId}-${response?.stepId}-${response?.isSuccess}`}
                >
                  <span className="kuika-step-screen-status__headline">
                    <KuikaSvg
                      name={isSuccess ? "circle-check" : "circle-x"}
                      width={16}
                      height={16}
                      color={isSuccess ? "var(--green-6)" : "var(--red-5)"}
                    />
                    {/* The word carries the outcome; the colour only seconds it. */}
                    <span
                      className={classNames("kuika-step-screen-status__state", {
                        "kuika-step-screen-status__state--failed": !isSuccess
                      })}
                    >
                      {isSuccess ? "Success" : "Failed"}
                    </span>
                    <span className="kuika-step-screen-status__step">
                      {this.getProcessName(response?.processId)} ›{" "}
                      {this.getStepName(response?.processId, response?.stepId)}
                    </span>
                  </span>
                  {!isSuccess && (
                    <span className="kuika-step-screen-status__reason">
                      Reason: {response.message ?? "Unknown. Please reach us from help desk for support."}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </KModal>
      </>
    );
  }
}

const stepScreenFormWizard = withSD<IStepScreenFormProps>(StepScreenFormWizard);
export { stepScreenFormWizard as StepScreenFormWizard };
